import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { supabase, saveLessonAttendanceBulkRPC } from "../../../lib/supabase";
import { getAttendanceBundle } from "../../../lib/attendanceBundle";
import { attendanceApi } from "../api/attendanceApi";
import { teacherDashboardApi } from "../../teacherDashboard/api/teacherDashboardApi";
import { rootApi } from "../../../lib/api/rootApi";
import { useAuth } from "../../../contexts/AuthContext";
import {
  getCourseStudentIds,
  buildConsecutiveAttendance,
} from "../utils/consecutiveLessonHelpers";
import { writeLessonSaveHints } from "../utils/lessonSaveHintsCache";

import type {
  AttendanceSaveHints,
  TempAttendanceMap,
  TempAttendanceEntry,
} from "../types/attendanceDialog";

// ============================================================================
// Types
// ============================================================================

export interface UseAttendanceSaveArgs {
  selectedLesson: any | null;
  tempAttendance: TempAttendanceMap;
  lessonStatusOverrides: Record<string, { status?: string; note?: string }>;
  overrideReasons: Record<string, string>;
  lessonNote: string;
  /**
   * Hausaufgaben for this lesson. Persisted via the diary upsert RPC
   * (separate from `save_lesson_attendance_bulk`'s entry_text write).
   * Only fires when the field was actually edited — see `initialLessonHomework`.
   */
  lessonHomework?: string;
  /**
   * Pristine homework value at load time. The save hook fires the
   * diary-upsert RPC only when `lessonHomework !== initialLessonHomework`,
   * so attendance saves on lessons without homework changes stay at 1 RPC.
   */
  initialLessonHomework?: string;
  onSaved?: (lessonId: string, hints?: AttendanceSaveHints) => void;
  onOpenChange: (open: boolean) => void;
  /**
   * Loader-owned ref that, when true, tells the bundle realtime subscription
   * to skip its refetch. Prefer `pauseRealtime` over mutating this directly —
   * the loader has its own release timer that can pre-empt a raw flag flip.
   */
  suppressRealtimeRef?: React.MutableRefObject<boolean>;
  /**
   * Loader-provided pause helper that atomically clears any pending release
   * timer and re-arms the suppression for `ms` milliseconds. The save flow
   * uses this instead of `suppressRealtimeRef.current = true` so a load-effect
   * release timer can't re-enable the realtime mid-save.
   */
  pauseRealtime?: (ms: number) => void;
  /**
   * Override for the teacher user_id (used as `recorded_by` on save).
   * Required for the paired-kiosk path where there's no signed-in
   * React user — without this the save throws "Kein gültiges
   * Benutzerprofil gefunden (recorded_by)" *before* the request even
   * goes out, because the hook falls back to `userProfile.id` from
   * `useAuth()` which is null. Kiosk callers pass the teacher's
   * user_id (from the chip-login response, `session.user.id`).
   */
  userIdOverride?: string | null;
}

export interface UseAttendanceSaveReturn {
  /**
   * Persist attendance to the RPC. Pass `entryOverrides` to merge per-student
   * fields onto the current `tempAttendance` *for this save only* — used by
   * the earlier-present save guard so freshly-set `leftSchool` /
   * `elsewhereLocation` flags don't have to round-trip through React state
   * before the save fires.
   */
  saveAttendance: (
    entryOverrides?: Record<string, Partial<TempAttendanceEntry>>,
  ) => Promise<void>;
}

// ============================================================================
// Hook
// ============================================================================

export function useAttendanceSave({
  selectedLesson,
  tempAttendance,
  lessonStatusOverrides,
  overrideReasons,
  lessonNote,
  lessonHomework,
  initialLessonHomework,
  onSaved,
  onOpenChange,
  suppressRealtimeRef,
  pauseRealtime,
  userIdOverride,
}: UseAttendanceSaveArgs): UseAttendanceSaveReturn {
  const dispatch = useDispatch();
  const { userProfile } = useAuth();
  // Pulled from context so we don't have to round-trip through
  // supabase.auth.getUser() inside saveLessonAttendanceBulkRPC for every save.
  // For the paired-kiosk path, `userProfile` is null (no signed-in
  // user) — callers pass `userIdOverride` instead, sourced from
  // the chip-login session. See UseAttendanceSaveArgs docstring.
  const recordedBy = userIdOverride ?? (userProfile as any)?.id ?? null;

  const saveAttendance = useCallback(async (
    entryOverrides?: Record<string, Partial<TempAttendanceEntry>>,
  ) => {
    if (!selectedLesson) return;
    // Suppress the loader's realtime subscription for the duration of the
    // save + a short tail. The subscription is debounced 500ms and re-fires
    // a `get_attendance_bundle_v2` for every SAL insert; without this guard
    // a single save produces an extra bundle round-trip the loader doesn't
    // need (the dialog is closing).
    //
    // Prefer pauseRealtime because the loader has its own release timer
    // (post-load) that can otherwise pre-empt a raw flag flip mid-save.
    if (pauseRealtime) {
      pauseRealtime(2500);
    } else if (suppressRealtimeRef) {
      suppressRealtimeRef.current = true;
    }
    const readEntry = (sid: string): TempAttendanceEntry | undefined => {
      const base = tempAttendance[sid];
      const ov = entryOverrides?.[sid];
      if (!base && !ov) return undefined;
      return { ...(base as TempAttendanceEntry), ...(ov || {}) };
    };
    // Use already-loaded student data from bundle instead of fetching again
    const students = (selectedLesson.students || []) as {
      id: string;
      name: string;
    }[];
    const nameToId = new Map(
      students.map((s) => [(s.name || "").trim().toLowerCase(), s.id]),
    );
    const schoolId = selectedLesson.school_id;
    // Map UI status to DB attendance_status.
    //
    // The "U" cell in the lesson dialog is now labeled "Ungeklärt" —
    // teachers hitting it haven't decided "formally unentschuldigt",
    // they're just saying "this kid wasn't here, no klärung yet".
    // We write `absent_unclear`; the formal `absent_unexcused` only
    // comes from the StatusPicker in the morning-report widget.
    const mapStatus = (s: any, lateExcused?: boolean) => {
      if (s === "present") return "present";
      if (s === "late") return lateExcused ? "late_excused" : "late_unexcused";
      if (s === "excused") return "absent_excused";
      return "absent_unclear";
    };
    let attendanceArray: any[] = [];
    // Track students whose absence in *this* lesson was "Anderswo" (still in
    // school, just somewhere else) so the consecutive-lesson copy can flip
    // them back to `present` instead of carrying the absent status forward.
    const anderswoStudentIds = new Set<string>();
    const validIds = new Set(
      (selectedLesson.students || []).map((s: any) => s.id),
    );
    const markedIds = new Set(Object.keys(tempAttendance || {}));
    const overrideIds = new Set(Object.keys(lessonStatusOverrides || {}));
    const allIds = Array.from(
      new Set<string>([...Array.from(markedIds), ...Array.from(overrideIds)]),
    ).filter((sid) => validIds.has(sid));
    allIds.forEach((sid) => {
      const a = readEntry(sid);
      const so = (lessonStatusOverrides as any)[sid];
      let statusOut: string | null = null;
      if (a?.status) statusOut = mapStatus(a.status, a?.lateExcused);
      else if (so?.status) statusOut = so.status as string;
      if (!statusOut) return;
      // `leftSchool` is the earlier-present-dialog's "Auschecken" signal. It
      // used to flip statusOut → 'left_early', but the bulk RPC dropped
      // left_early from the per-lesson axis (it's a day-level presence value
      // now, written by the actual check-out flow). We keep statusOut as the
      // teacher's choice (absent_excused / absent_unclear) — the dialog's
      // free-text Begründung still lands on attendance_logs.notes below via
      // the excuseReason branch.
      const studentObj = (selectedLesson.students || []).find(
        (s: any) => s.id === sid,
      );
      const normalizedName = (studentObj?.name || "").trim().toLowerCase();
      const resolvedId =
        (normalizedName && nameToId.get(normalizedName)) || sid;
      let note = null as string | null;
      let lateMinutesComputed: number | undefined = undefined;
      let arrivalOut: string | undefined = undefined;
      if (
        (a?.status === "excused" || a?.status === "unexcused") &&
        a.excuseReason
      ) {
        note = a.excuseReason;
      }
      if (a?.status === "late") {
        // Prefer arrivalTime to derive minutes; fallback to a.minutesLate
        if (a.arrivalTime) {
          arrivalOut = a.arrivalTime;
          try {
            const [ah, am] = a.arrivalTime
              .split(":")
              .map((n: string) => parseInt(n, 10));
            const start = new Date(selectedLesson.start_datetime);
            const arrival = new Date(start);
            arrival.setHours(ah, am || 0, 0, 0);
            const diff = Math.max(
              0,
              Math.round((arrival.getTime() - start.getTime()) / 60000),
            );
            lateMinutesComputed = diff;
          } catch {}
        }
        if (
          typeof a.minutesLate === "number" &&
          (lateMinutesComputed === undefined || lateMinutesComputed === 0)
        ) {
          lateMinutesComputed = a.minutesLate;
        }
        const parts: string[] = [];
        if (arrivalOut) parts.push(`Ankunft: ${arrivalOut}`);
        if (typeof lateMinutesComputed === "number")
          parts.push(`${lateMinutesComputed} Min. verspätet`);
        if (a.lateExcused) parts.push("Entschuldigt");
        note = parts.join(" | ") || null;
      }
      const ovr = overrideReasons[resolvedId] || overrideReasons[sid];
      if (ovr && ovr.trim())
        note = note
          ? `${note} | Override: ${ovr.trim()}`
          : `Override: ${ovr.trim()}`;
      if (so?.note) note = note ? `${note} | ${so.note}` : so.note;
      if (
        a?.elsewhereLocation &&
        a.elsewhereLocation.trim() &&
        !a.leftSchool
      ) {
        const loc = `Anderswo: ${a.elsewhereLocation.trim()}`;
        note = note ? `${note} | ${loc}` : loc;
        anderswoStudentIds.add(resolvedId);
      }
      attendanceArray.push({
        student_id: resolvedId,
        status: statusOut,
        note,
        late_minutes: lateMinutesComputed,
        arrival_time: arrivalOut,
      });
    });
    // Daily log updates are handled inside save_lesson_attendance_bulk RPC

    await saveLessonAttendanceBulkRPC({
      lessonId: selectedLesson.id,
      schoolId,
      attendance: attendanceArray,
      diaryText: lessonNote || null,
      diaryPrivate: false,
      recordedBy,
    });

    // Homework — only fire the diary-upsert RPC when the teacher actually
    // edited the Hausaufgaben field. The attendance-bulk RPC above already
    // wrote entry_text; we re-upsert both fields together here so homework
    // changes can't accidentally null out entry_text (the upsert REPLACES
    // both columns from EXCLUDED).
    const hwCurrent = (lessonHomework ?? "").trim();
    const hwInitial = (initialLessonHomework ?? "").trim();
    if (hwCurrent !== hwInitial) {
      try {
        await supabase.rpc("fn_klassenbuch_upsert_diary_entry" as never, {
          p_lesson_id: selectedLesson.id,
          p_entry_text: lessonNote || "",
          p_homework: hwCurrent === "" ? null : hwCurrent,
        } as never);
        // Mirror what the RTK upsertDiaryEntry mutation does — bypassing
        // it (we used supabase.rpc directly) means we must invalidate the
        // diary cache manually so the Unterrichtsliste / diary view picks
        // up the homework on its next render.
        dispatch(
          rootApi.util.invalidateTags([
            { type: "KlassenbuchDiary" as never, id: "ENTRIES" },
          ]),
        );
      } catch (err) {
        console.warn(
          "⚠️ [AttendanceDialog] Hausaufgaben save failed:",
          err,
        );
      }
    }

    // Double lesson logic: find and save to consecutive lesson (same class, subject, teacher)
    try {
      const currentEnd = new Date(selectedLesson.end_datetime);
      const classId = selectedLesson.class_id;
      // The selectedLesson object has 'subject' not 'subject_name', and 'teacher' might be missing
      const subjectName = selectedLesson.subject; // mapped from lessonData.subject_name at line 187
      const primaryTeacherId = selectedLesson.primary_teacher_id;

      if (classId && subjectName) {
        // Find next lesson with same class and subject, starting within 15 min of current end
        // NOTE: Database stores timestamps WITHOUT timezone (local Berlin time)
        // We need to format as local time string, not UTC ISO string
        const endTimeStr = selectedLesson.end_datetime; // e.g., "2026-01-12T08:45:00" or "2026-01-12 08:45:00"
        // Parse and add 15 min for max range
        const endMs = currentEnd.getTime();
        const maxMs = endMs + 15 * 60000;
        // Format as local time string (YYYY-MM-DD HH:mm:ss)
        const formatLocal = (d: Date) => {
          const pad = (n: number) => n.toString().padStart(2, "0");
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
            d.getDate(),
          )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
            d.getSeconds(),
          )}`;
        };
        // Use the raw end_datetime from lesson (already in local time)
        const gteStart =
          typeof endTimeStr === "string"
            ? endTimeStr.replace("T", " ").slice(0, 19)
            : formatLocal(currentEnd);
        const maxStartTime = formatLocal(new Date(maxMs));

        const { data: nextLessons } = await (supabase as any)
          .from("vw_react_lesson_details_all")
          .select(
            "lesson_id, start_datetime, end_datetime, class_id, subject_name, primary_teacher_id",
          )
          .eq("school_id", schoolId)
          .eq("class_id", classId)
          .eq("subject_name", subjectName)
          .gte("start_datetime", gteStart)
          .lte("start_datetime", maxStartTime)
          .neq("lesson_id", selectedLesson.id) // Exclude current lesson
          .order("start_datetime", { ascending: true })
          .limit(1);

        if (nextLessons && nextLessons.length > 0) {
          const nextLesson = nextLessons[0];

          // Optional: also check teacher matches (if both have teachers)
          const teacherMatches =
            !primaryTeacherId ||
            !nextLesson.primary_teacher_id ||
            primaryTeacherId === nextLesson.primary_teacher_id;

          if (teacherMatches) {
            // Fetch the next lesson's bundle to check for concurrent courses
            // Students enrolled in active (non-cancelled) courses should NOT
            // receive carry-over attendance — they belong to their course lesson.
            //
            // OPTIMIZATION: skip the bundle fetch if THIS lesson already has
            // no concurrent courses. Concurrent course assignments rarely
            // appear in the next consecutive lesson (within 15 min, same
            // class+subject) when they don't exist in this one — the
            // assignments are tied to the period block, and back-to-back
            // periods of the same subject typically share the same overlay.
            // Saves one full bundle RPC per save in the common case.
            let courseStudentIds = new Set<string>();
            const thisLessonHasCourses = !!(
              (selectedLesson as any)?.concurrent_course_attendance?.courses ||
              []
            ).length;
            if (thisLessonHasCourses) {
              try {
                const nextBundle = await getAttendanceBundle(
                  nextLesson.lesson_id,
                  schoolId,
                );
                courseStudentIds = getCourseStudentIds(
                  nextBundle.concurrent_course_attendance,
                );
              } catch (err) {
                console.warn(
                  "⚠️ [AttendanceDialog] Failed to fetch next lesson bundle for course exclusion:",
                  err,
                );
                // Continue without exclusion — better to save all than fail entirely
              }
            }

            const consecutiveAttendance = buildConsecutiveAttendance(
              attendanceArray,
              courseStudentIds,
              anderswoStudentIds,
            );

            if (consecutiveAttendance.length > 0) {
              await saveLessonAttendanceBulkRPC({
                lessonId: nextLesson.lesson_id,
                schoolId,
                attendance: consecutiveAttendance as any,
                diaryText: null,
                diaryPrivate: false,
                recordedBy,
              });

              // Invalidate cache for consecutive lesson too
              // Same rationale as the main lesson: skip invalidateTags to
              // avoid waking the loader's lazy subscription with a refetch
              // the dialog can't consume.

              // Stash tile-refresh hints for the consecutive lesson too —
              // the realtime subscription will pick the lesson up and the
              // hint cache lets us skip the 3-query refresh round-trip.
              writeLessonSaveHints(nextLesson.lesson_id, {
                markedCount: consecutiveAttendance.length,
                studentCount:
                  ((selectedLesson?.students || []) as any[]).length ||
                  undefined,
              });
            }

            if (courseStudentIds.size > 0) {
              console.info(
                `ℹ️ [AttendanceDialog] Excluded ${courseStudentIds.size} student(s) from consecutive lesson — enrolled in concurrent courses`,
              );
            }
          }
        } else {
        }
      } else {
      }
    } catch (err) {
      console.warn(
        "⚠️ [AttendanceDialog] Failed to save consecutive lesson attendance:",
        err,
      );
    }

    // NOTE: Lateness is now stored in student_attendance_logs only, not as absence notes.
    // The lateness details (minutes, excused) are saved by save_lesson_attendance_bulk RPC.

    // NOTE: Removed per-student ensure_absence_note loop - save_lesson_attendance_bulk RPC now handles
    // absence note creation as part of the bulk save operation. This eliminated ~4 requests per absent student.

    // NOTE: We deliberately do NOT invalidate the AttendanceBundle tag here.
    // The loader's lazy query subscription stays alive for `keepUnusedDataFor`
    // (60s) after the dialog unmounts, so invalidating triggers an immediate
    // background refetch the closing dialog can't even consume. The loader
    // now always fetches fresh on open (`preferCacheValue=false`), so stale
    // cache isn't a concern.

    // Invalidate teacher dashboard so badge refetches from the RPC
    // (which includes concurrent_deduct logic for correct totals)
    dispatch(
      teacherDashboardApi.util.invalidateTags(["TeacherDashboard"]),
    );

    // Invalidate the Klärungen REPORT so "Mein Klassenbericht" picks
    // up the new absent_unclear / absent_excused rows immediately —
    // otherwise newly-U-marked students only show up after a manual
    // page refresh.
    dispatch(
      rootApi.util.invalidateTags([{ type: "Klaerungen", id: "REPORT" }]),
    );

    // Hand the parent everything it needs to update the lesson tile without
    // hitting the network: marked-rows count + roster size we just used.
    // Stash hints in the module-level cache so the stundenplan tile-refresh
    // hook can pick them up without us having to thread another arg through
    // the prop chain. `onSaved` callers that *do* care about the hints can
    // also read them from the second argument.
    const totalStudents =
      ((selectedLesson?.students || []) as any[]).length || undefined;
    const saveHints = {
      markedCount: attendanceArray.length,
      studentCount: totalStudents,
    };
    writeLessonSaveHints(selectedLesson.id, saveHints);
    onSaved && onSaved(selectedLesson.id, saveHints);
    onOpenChange(false);

    // Suppression auto-releases via pauseRealtime's internal timer; nothing
    // to do here when pauseRealtime is wired. Legacy callers that only pass
    // the ref get a manual release.
    if (!pauseRealtime && suppressRealtimeRef) {
      const ref = suppressRealtimeRef;
      setTimeout(() => {
        ref.current = false;
      }, 1500);
    }
  }, [
    selectedLesson,
    tempAttendance,
    lessonStatusOverrides,
    overrideReasons,
    lessonNote,
    lessonHomework,
    initialLessonHomework,
    onSaved,
    onOpenChange,
    dispatch,
    suppressRealtimeRef,
    pauseRealtime,
    recordedBy,
  ]);

  return { saveAttendance };
}
